import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuotaPanelState } from '../hooks/useQuotaPanelState';

// Window labels resolved with literal translation keys (the static integrity
// guard requires every translation argument to be a string literal).
const windowLabelFor = (t, windowType) => {
  switch (windowType) {
    case 'month': return t('the current month');
    case 'isoQuarter': return t('the current quarter');
    case 'fixed': return t('the defined period');
    default: return t('the period');
  }
};

function buildExceededMessage(t, { limit, filters, window }) {
  const windowType = window?.type || 'none';
  const windowLabel = windowType === 'rollingDays'
    ? t('the last {{count}} days', { count: window?.days })
    : windowLabelFor(t, windowType);

  const parts = [
    t('Quota reached: max {{count}} request', { count: limit }),
  ];

  const customFields = filters?.customFields || [];
  const fieldParts = customFields.map((f) => f.value);

  if (fieldParts.length > 1) {
    parts.push(fieldParts.join(' / '));
  }

  parts.push(t('over {{period}}.', { period: windowLabel }));

  return parts.join(' ');
}

const ProgressBar = ({ value, max }) => {
  const safeMax = Math.max(1, Number(max) || 1);
  const safeValue = Math.min(safeMax, Math.max(0, Number(value) || 0));
  return (
    <progress
      value={safeValue}
      max={safeMax}
      className="w-full h-2 overflow-hidden rounded bg-gray-200 [&::-webkit-progress-bar]:bg-gray-200 [&::-webkit-progress-value]:bg-primary [&::-moz-progress-bar]:bg-primary"
    />
  );
};

export default function QuotaPanel({
  enabled,
  serviceDeskId,
  requestTypeId,
  requiredFieldIds = [],
  formData,
  onQuotaStateChange = () => {},
}) {
  const { t } = useTranslation();
  const {
    hasQuota,
    countLoading,
    checkLoading,
    checkData,
    checkError,
    checking,
    blocking,
  } = useQuotaPanelState({
    enabled,
    serviceDeskId,
    requestTypeId,
    requiredFieldIds,
    formData,
  });

  // Expose state to parent (disable submit, etc.)
  useEffect(() => {
    onQuotaStateChange({ checking, blocking });
  }, [checking, blocking, onQuotaStateChange]);

  if (!enabled) return null;
  if (!hasQuota && !countLoading && !checkError) return null;
  if (!hasQuota && countLoading) return null; // keep it transparent until we know

  // Panel shown whenever requestType has quotas configured.
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-4">
      <div className="text-sm font-semibold text-gray-900">{t('Quota')}</div>

      {!checkData && (
        <div className="mt-1 text-sm text-gray-700">
          {t('This request type is subject to quota management. The quota will be computed once all required fields are filled in.')}
        </div>
      )}

      {checkLoading && (
        <div className="mt-3 text-sm text-gray-600">{t('Checking the quota…')}</div>
      )}

      {!checkLoading && checkError && (
        <div className="mt-3 text-sm text-red-700">
          {t('Quota check failed.')}
        </div>
      )}

      {!checkLoading && checkData && checkData.status === 'no_quota_found' && (
        <div className="mt-3 text-sm text-gray-700">
          {t('No quota applies to the current selection.')}
        </div>
      )}

      {!checkLoading && checkData && Array.isArray(checkData.rules) && checkData.rules.length > 0 && (
        <div className="mt-4 space-y-3">
          {checkData.rules.map(({ id, limit, count, exceeded, filters, window }) => {
            return (
              <div key={id} className="rounded border border-gray-100 bg-gray-50 px-3 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-medium text-gray-900">&nbsp;</div>
                  <div className={`text-sm ${exceeded ? 'text-red-700' : 'text-gray-700'}`}>
                    {count} / {limit}
                  </div>
                </div>

                <div className="mt-2">
                  <ProgressBar value={Math.min(count, limit)} max={Math.max(1, limit)} />
                </div>

                {exceeded ? (
                  <div className="mt-2 text-xs">
                    <p className="text-red-700">{buildExceededMessage(t, { limit, filters, window })}</p>
                    <p className="text-amber-700">{t('Your request will be created as a draft so you can submit it once the quotas are reset for the next period.')}</p>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm text-gray-700">{t('Maximum quota not reached, you can submit a new request.')}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


